import { ExclamationCircle } from 'flowbite-react-icons/outline'
import { Button } from '@helix-ds/react'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ProviderAPIClient from '../ProviderAPIClient'
import { useAuthorization } from '@/features/authorization/authorizationHooks'
import { homePathForPermissions } from '@/features/auth/homeRouting'

/**
 * Standalone callback handler for Auth0 passwordless authentication
 * Waits for Auth0 authentication to complete, then checks if it's a first-time login
 * Redirects to /welcome for first-time users, else to the permission-aware home
 * (`/cases` for org-less case-only users, `/orders` for org members).
 */
const StandaloneCallback: React.FC = () => {
  const { isAuthenticated, isLoading, error, getAccessTokenSilently } = useAuth0()
  const { permissions, isLoading: authzLoading } = useAuthorization()
  const navigate = useNavigate()
  const [retryCount, setRetryCount] = useState(0)
  const maxRetries = 15

  useEffect(() => {
    let active = true
    let retryTimeout: ReturnType<typeof setTimeout> | null = null

    const handleCallback = async () => {
      try {
        await Sentry.startSpan(
          {
            op: 'auth.callback',
            name: 'Standalone Auth0 Callback',
          },
          async (span) => {
            span.setAttribute('auth.flow', 'standalone')

            // If there's an error, log it and show error state
            if (error) {
              span.setAttribute('error', true)
              Sentry.captureException(new Error(`Auth0 callback error: ${error.message || error}`), {
                tags: {
                  operation: 'auth.callback',
                  flow: 'standalone',
                },
              })
              return
            }

            // Wait for Auth0 to finish loading
            if (isLoading) {
              if (retryCount < maxRetries && active) {
                const delay = Math.min(500 * Math.pow(1.5, retryCount), 2000)
                retryTimeout = setTimeout(() => {
                  if (active) {
                    setRetryCount((prev) => prev + 1)
                  }
                }, delay)
              }
              return
            }

            // Check if authenticated
            if (!isAuthenticated) {
              if (retryCount < maxRetries && active) {
                const delay = Math.min(500 * Math.pow(1.5, retryCount), 2000)
                retryTimeout = setTimeout(() => {
                  if (active) {
                    setRetryCount((prev) => prev + 1)
                  }
                }, delay)
              } else if (active) {
                // Max retries reached, redirect to login
                span.setAttribute('max_retries_reached', true)
                navigate('/login', { replace: true })
              }
              return
            }

            // Authentication successful - check if first-time login
            try {
              // Initialize API client with Auth0 token getter
              const apiClient = ProviderAPIClient.getInstance(getAccessTokenSilently)

              // Call backend to check if this is first-time login
              const firstLoginResponse = await apiClient.get('/auth/check-first-login')

              if (!firstLoginResponse.ok) {
                throw new Error(`Failed to check first login: ${firstLoginResponse.status}`)
              }

              const firstLoginData = await firstLoginResponse.json()
              const isFirstLogin = firstLoginData.isFirstLogin

              span.setAttribute('is_first_login', isFirstLogin)
              span.setAttribute('logins_count', firstLoginData.loginsCount)

              if (active) {
                // Navigate to welcome page for first-time users, otherwise to
                // the permission-aware home. For returning users, wait until the
                // authorization context has resolved permissions so a case-only
                // (org-less) user is routed to /cases rather than dead-ending on
                // /orders. While perms are still loading, retry (bounded).
                if (isFirstLogin) {
                  navigate('/welcome', { replace: true })
                } else if (authzLoading) {
                  if (retryCount < maxRetries) {
                    const delay = Math.min(500 * Math.pow(1.5, retryCount), 2000)
                    retryTimeout = setTimeout(() => {
                      if (active) {
                        setRetryCount((prev) => prev + 1)
                      }
                    }, delay)
                  } else {
                    // Perms never resolved in time — safe default.
                    navigate('/orders', { replace: true })
                  }
                } else {
                  navigate(homePathForPermissions(permissions), { replace: true })
                }
              }
            } catch (firstLoginError) {
              Sentry.captureException(firstLoginError, {
                tags: {
                  operation: 'auth.check_first_login',
                  flow: 'standalone',
                },
              })
              // On error checking first login, default to orders page (safe fallback)
              if (active) {
                navigate('/orders', { replace: true })
              }
            }
          }
        )
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            operation: 'auth.callback',
            flow: 'standalone',
          },
        })
        // On error, still try to navigate to orders
        if (active) {
          navigate('/orders', { replace: true })
        }
      }
    }

    handleCallback()

    return () => {
      active = false
      if (retryTimeout) {
        clearTimeout(retryTimeout)
      }
    }
  }, [isAuthenticated, isLoading, error, navigate, getAccessTokenSilently, retryCount, permissions, authzLoading])

  // Show error state
  if (error && !isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-secondary-medium dark:bg-gray-800">
        <div className="w-full max-w-md p-8">
          <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-8 shadow-lg">
            <div className="mt-8">
              <div className="text-center">
                <div className="mb-4 text-text-fg-error dark:text-error-100">
                  <ExclamationCircle className="mx-auto h-12 w-12" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-text-heading dark:text-white">Authentication Error</h3>
                <p className="mb-4 text-sm text-text-body dark:text-gray-400">
                  {error.message || 'An error occurred during authentication'}
                </p>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => navigate('/login', { replace: true })}
                  className="focus-visible:ring-brand-600"
                >
                  Return to Login
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show loading state
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-secondary-medium dark:bg-gray-800">
      <div className="w-full max-w-md p-8">
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-8 shadow-lg">
          <div className="mt-8">
            <div className="text-center">
              <div
                className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-600 dark:border-brand-500 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              >
                <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                  Loading...
                </span>
              </div>
              <p className="mt-2 text-sm text-text-body dark:text-gray-400">Completing authentication...</p>
              {retryCount > 0 && (
                <p className="mt-1 text-xs text-text-body dark:text-gray-400">
                  Attempt {retryCount} of {maxRetries}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StandaloneCallback
